import type { EarlyCheckinStage } from '../types/earlyCheckin';
import type {
  ProjectDashboardCoachCount,
  ProjectDashboardCompetenceCount,
  ProjectDashboardSnapshot,
  ProjectDashboardStudentRow,
} from '../types/projectDashboard';
import { mapStudentClassificationFromColumn } from '../types/studentClassification';
import { isEarlyCheckinStage } from './earlyCheckin';
import { mapProjectIdentity } from './project';

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toOptionalNumber = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toOptionalString = (value: unknown): string | null => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

const mapEntriesByCoach = (raw: unknown): ProjectDashboardCoachCount[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const row = item as Record<string, unknown>;
      const coachName = typeof row.coachName === 'string' ? row.coachName : '';
      if (!coachName) {
        return null;
      }

      return {
        coachId: toNumber(row.coachId),
        coachName,
        total: toNumber(row.total),
      };
    })
    .filter((item): item is ProjectDashboardCoachCount => item != null);
};

const mapStudentsByCompetence = (raw: unknown): ProjectDashboardCompetenceCount[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const row = item as Record<string, unknown>;
      return {
        competenceMonth: toOptionalString(row.competenceMonth),
        total: toNumber(row.total),
      };
    })
    .filter((item): item is ProjectDashboardCompetenceCount => item != null);
};

export function mapProjectDashboardSnapshot(raw: unknown): ProjectDashboardSnapshot {
  const payload = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  return {
    generatedAt: typeof payload.generatedAt === 'string'
      ? payload.generatedAt
      : new Date().toISOString(),
    project: mapProjectIdentity(payload.project),
    entriesByCoach: mapEntriesByCoach(payload.entriesByCoach),
    studentsByCompetence: mapStudentsByCompetence(payload.studentsByCompetence),
  };
}

export function mapProjectDashboardStudentRow(raw: unknown): ProjectDashboardStudentRow | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const row = raw as Record<string, unknown>;
  const studentId = toOptionalNumber(row.studentId ?? row.student_id);
  const studentName = toOptionalString(row.studentName ?? row.student_name);
  if (studentId == null || !studentName) {
    return null;
  }

  const stageRaw = toOptionalString(row.fupStage ?? row.fup_stage);
  const fupStage: EarlyCheckinStage | null = stageRaw && isEarlyCheckinStage(stageRaw)
    ? stageRaw
    : null;

  return {
    studentId,
    studentName,
    studentEmail: toOptionalString(row.studentEmail ?? row.student_email),
    joinedAt: toOptionalString(row.joinedAt ?? row.joined_at),
    classification: mapStudentClassificationFromColumn(
      toOptionalString(row.classification),
    ),
    coachId: toOptionalNumber(row.coachId ?? row.coach_id),
    coachName: toOptionalString(row.coachName ?? row.coach_name) ?? 'Sem professora',
    fupStage,
  };
}

export function formatProjectCompetenceLabel(competenceMonth: string | null): string {
  if (!competenceMonth) {
    return 'Sem data de adesão';
  }

  const match = /^(\d{4})-(\d{2})$/.exec(competenceMonth);
  if (!match) {
    return competenceMonth;
  }

  return `${match[2]}/${match[1]}`;
}
